import { forwardRef, memo, type HTMLAttributes } from 'react';
import { cva, type CVAVariantProps } from '../../utils/variants';
import { cn } from '../../utils/cn';
import type { BaseComponentProps } from '../../types';

/**
 * Varianti del Container
 */
const containerVariants = cva(
  'mx-auto w-full',
  {
    variants: {
      maxWidth: {
        sm: 'max-w-screen-sm',   // 640px
        md: 'max-w-screen-md',   // 768px
        lg: 'max-w-screen-lg',   // 1024px
        xl: 'max-w-screen-xl',   // 1280px
        '2xl': 'max-w-screen-2xl', // 1536px
        full: 'max-w-full',
      },
      padding: {
        none: '',
        sm: 'px-4',
        md: 'px-6',
        lg: 'px-8',
        xl: 'px-12',
      },
    },
    defaultVariants: {
      maxWidth: 'xl',
      padding: 'md',
    },
  }
);

export interface ContainerProps
  extends HTMLAttributes<HTMLDivElement>,
    BaseComponentProps,
    CVAVariantProps<typeof containerVariants> {
  /**
   * Centra il contenuto verticalmente
   */
  centerY?: boolean;
}

/**
 * Componente Container
 * 
 * Container responsive con max-width configurabile e padding.
 * Ottimo per layout consistenti.
 * 
 * @example
 * ```tsx
 * <Container maxWidth="lg">
 *   <h1>Contenuto centrato</h1>
 * </Container>
 * ```
 */
const ContainerComponent = forwardRef<HTMLDivElement, ContainerProps>(
  (
    {
      className,
      maxWidth,
      padding,
      centerY,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          containerVariants({ maxWidth, padding }),
          centerY && 'flex items-center min-h-screen',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

ContainerComponent.displayName = 'Container';

export const Container = memo(ContainerComponent);

